import { createSlice } from "@reduxjs/toolkit"
import { notification } from "antd"
import moment from "moment"

// http client
import client from "../../lib/client"
import {
    billsUrl,
    billUrl,
    dynamicUrl,
    clientBillableAccountSelectOptionsUrl,
    billableAccountSelectOptionByIdUrl,
    billFilteredBillableAccountSelectOptionsUrl,
    clientSupplyAccountSelectOptionsUrl,
    supplyAccountSelectOptionByIdUrl,
    billFilteredSupplyAccountSelectOptionsUrl,
    previousBillableAccountBillsUrl,
    previousSupplyAccountBillsUrl,
    billAccountUrl,
    linkedServiceBillSelectOptionsUrl,
    serviceBillUrl,
    groupAccountBillsUrl,
    billsScopedByClientUrl,
    billsCsvUrl,
    groupAccountBillsScopedByClientUrl,
    clientUrl,
} from "../../lib/endpoints"
import { createFilterUrl } from "../../functions/objectFunctions/createFilterUrl"
import { createRansackParams } from "../../functions/objectFunctions/createRansackParams"

const emptyState = {
    bill: {},
    bills: [],
    billSelectOptions: [],
    billSearch: [],
    account: {},
    accountSelectOptions: [],
    previousBillSelectOptions: [],
    previousBill: {},
    linkedServiceBillSelectOptions: [],
    total: 0,
    pagination: { current: 1, pageSize: 10 },
    loading: false,
    filters: {},
    sort: {
        column: "created_at",
        direction: "desc",
    },
}

export const billsSlice = createSlice({
    name: "bills",
    initialState: emptyState,
    reducers: {
        setBill: (state, action) => {
            state.bill = action.payload
        },
        setBills: (state, action) => {
            state.bills = action.payload.bills
            state.total = action.payload.total
        },
        setPagination: (state, action) => {
            state.pagination = action.payload
        },
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setAccountLoading: (state, action) => {
            state.accountLoading = action.payload
        },
        setBillSelectOptions: (state, action) => {
            state.billSelectOptions = action.payload
        },
        setBillSearch: (state, action) => {
            state.billSearch = action.payload
        },
        setAccount: (state, action) => {
            state.account = action.payload
        },
        setAccountSelectOptions: (state, action) => {
            state.accountSelectOptions = action.payload
        },
        setPreviousBillSelectOptions: (state, action) => {
            state.previousBillSelectOptions = action.payload
        },
        setPreviousBill: (state, action) => {
            state.previousBill = action.payload
        },
        setLinkedServiceBillSelectOptions: (state, action) => {
            state.linkedServiceBillSelectOptions = action.payload
        },
        setFilters: (state, action) => {
            state.filters = action.payload
        },
        setSort: (state, action) => {
            state.sort = action.payload
        },
        clearState: (state, action) => {
            return emptyState
        },
        deleteBill: (state, action) => {
            state.bills = state.bills.filter((bill) => bill.id !== action.payload)
            state.total = state.total - 1
        },
    },
})

export const {
    setBill,
    setBills,
    setPagination,
    setLoading,
    setAccountLoading,
    setBillSelectOptions,
    setBillSearch,
    setAccountSelectOptions,
    setAccount,
    setPreviousBillSelectOptions,
    setPreviousBill,
    setLinkedServiceBillSelectOptions,
    clearState,
    setFilters,
    setSort,
} = billsSlice.actions

export function fetchBill(id) {
    return async (dispatch, getState) => {
        client.get(billUrl(id)).then((response) => {
            dispatch(setBill(response.data))
        })
    }
}

export function fetchBills(pagination, currentPath) {
    // console.log(currentPath, pagination)
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        // This route may be nested so we have to use a dynamic route
        // /bills VS /clients/client_id/bills
        client.get(`${dynamicUrl(currentPath)}?page=${pagination.current}&per=${pagination.pageSize}`).then((response) => {
            dispatch(setPagination(pagination))
            dispatch(setBills(response.data))
            dispatch(setLoading(false))
        })
    }
}

export function fetchClientBills(clientId, pagination = null, currentPath = null) {
    return async (dispatch, getState) => {
        const clientBillsPath = `clients/${clientId}/bills/sidebar_records`

        const response = await client.get(`${dynamicUrl(clientBillsPath)}`)
        return response.data.bills
    }
}

export function fetchGroupAccountBills(pagination, groupAccountId) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        client.get(`${groupAccountBillsUrl(groupAccountId)}?page=${pagination.current}&per=${pagination.pageSize}`).then((response) => {
            dispatch(setPagination(pagination))
            dispatch(setBills(response.data))
            dispatch(setLoading(false))
        })
    }
}

// function checkForDaylightSavings(){
//     const today = new Date()
//     const month = today.getMonth() + 1 // Jan is 1, Dec is 12
//     const dayOfMonth = today.getDate()

//     // List of dates where DLS can start (for Mar) or end (for Nov)
//     const possibleDates = [1, 2, 3, 4, 5, 6, 7] // These are Nov dates, Mar dates need to adjust by 7 [8 -> 14]

//     if(month == 3 || month == 11){ 
//         const dlsStart = month == 3 ? true : false // boolean that checks whether DLS will start (Mar) or end (Nov)
//         const dateAdjust = dlsStart ? 7 : 0 // If it's Mar, adjust possibleDates by 7

//         if(dayOfMonth < possibleDates[0] + dateAdjust) return !dlsStart
//         if(dayOfMonth > possibleDates[6] + dateAdjust) return dlsStart 
        
//         // If sunday of that week is in range, DLS started/ended
//         const sundayOfWeek = new Date()
//         sundayOfWeek.setDate(sundayOfWeek.getDate() - sundayOfWeek.getDay())
    
//         if(possibleDates.includes(sundayOfWeek.getDate() - dateAdjust)) return dlsStart
//         else return !dlsStart
//     }

//     if(month > 3 && month < 11) return true // If date is before November and after March, return true. (DLS already started)
//     if(month < 3 || month > 11) return false // If date is after November and before March, return false. (DLS already ended)
// }

function checkForDaylightSavings() {
  const today = new Date();
  const year = today.getFullYear();

  // Second Sunday in March
  const march = new Date(year, 2, 1);
  const marchSundayOffset = (7 - march.getDay() + 7) % 7;
  const dstStart = new Date(year, 2, 1 + marchSundayOffset + 7);

  // First Sunday in November
  const nov = new Date(year, 10, 1);
  const novSundayOffset = (7 - nov.getDay()) % 7;
  const dstEnd = new Date(year, 10, 1 + novSundayOffset);

  return today >= dstStart && today < dstEnd;
}

// function dateToEST(date) {
//     if (date) {
//         const isDLS = checkForDaylightSavings()
//         const hoursToSub = isDLS ? 4 : 5
//         console.log(date)
//         date = new Date(date)
//         console.log(date)
//         date.setHours(date.getHours() - hoursToSub)
//         const result = date.toISOString()
//         return result
//     } else {
//         return null
//     }
// }

function dateToEST(dateInput) {
    if (!dateInput) return null;
    
    return new Date(dateInput).toISOString('en-US', {
        timeZone: 'America/New_York',
        hour12: false
    });
}

export function submitBill(values, id) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        // const date = dateToEST(values.date)
        // const due_date = dateToEST(values.due_date)
        const date = values.date
        const due_date = values.due_date

        const body = {
            bill: {
                ...values,
                date: date,
                due_date: due_date,
                service_bills_attributes: formatServiceBillDates(values.service_bills_attributes) || [],
                linked_service_bills_attributes: formatServiceBillDates(values.linked_service_bills_attributes) || [],
            },
        }

        // This endpoint used to answer 200 for both success and failure, so the caller only ever
        // branched on data.success. It now answers 422 with { success: false, message: [...] } when
        // it refuses an unlink, and axios rejects on 4xx -- without this catch the caller's `await`
        // throws, setLoading(false) never runs, and the save button spins forever with no message.
        try {
            const response = id ? await client.patch(billUrl(id), body) : await client.post(billsUrl, body)
            return response.data
        } catch (error) {
            if (error?.response?.data?.message) {
                return error.response.data
            }

            return { success: false, message: ["Could not save the bill. Please try again."] }
        }
    }
}

function formatServiceBillDates(serviceBills) {
    if (serviceBills) {
        const updatedServiceBills = serviceBills.map((serviceBill) => {
            return {
                ...serviceBill,
                fiscal_month: serviceBill.fiscal_month !== "" && serviceBill.fiscal_month != null ? new Date(serviceBill.fiscal_month).toISOString() : null,
                next_read_date: serviceBill.next_read_date !== "" && serviceBill.next_read_date != null ? new Date(serviceBill.next_read_date).toISOString() : null,
                previous_read_date: new Date(serviceBill.previous_read_date).toISOString() || null,
                read_date: new Date(serviceBill.read_date).toISOString() || null,
            }
            // return {
            //     ...serviceBill,
            //     fiscal_month: serviceBill.fiscal_month !== "" && serviceBill.fiscal_month != null ? dateToEST(serviceBill.fiscal_month) : null,
            //     next_read_date: serviceBill.next_read_date !== "" && serviceBill.next_read_date != null ? dateToEST(serviceBill.next_read_date) : null,
            //     previous_read_date: serviceBill.previous_read_date || null,
            //     read_date: dateToEST(serviceBill.read_date) || null,
            // }
        })
        return updatedServiceBills
    } else {
        return []
    }
}

export function deleteBill(id) {
    return async (dispatch, getState) => {
        client
            .delete(`${billUrl(id)}`)
            .then(({ data }) => {
                if (data.success) {
                    notification.success({
                        message: data.message,
                        duration: 0.8,
                    })

                    dispatch(billsSlice.actions.deleteBill(id))
                } else {
                    notification.error({
                        message: data.message,
                        duration: 0.8,
                    })
                }
            })
            .catch((e) => {
                notification.error({
                    message: "Request error",
                    description: e?.response?.statusText,
                    duration: 0.8,
                })
                console.error(e)
            })
    }
}

export function fetchClientBillableAccountSelectOptions(clientId) {
    return async (dispatch, getState) => {
        client.get(`${clientBillableAccountSelectOptionsUrl(clientId)}`).then((response) => {
            dispatch(setAccountSelectOptions(response.data.billable_accounts))
        })
    }
}

export function fetchBillableAccountSelectOptionById(id) {
    return async (dispatch, getState) => {
        client.get(`${billableAccountSelectOptionByIdUrl}?billable_account_id=${id}`).then((response) => {
            dispatch(setAccountSelectOptions(response.data.billable_accounts))
        })
    }
}

export function fetchFilteredBillableAccountSelectOptions(clientId, searchTerm) {
    return async (dispatch, getState) => {
        client.get(`${billFilteredBillableAccountSelectOptionsUrl(clientId, searchTerm)}`).then((response) => {
            // console.log(response.data.billable_accounts)
            dispatch(setAccountSelectOptions(response.data.billable_accounts))
        })
    }
}

export function fetchClientSupplyAccountSelectOptions(clientId) {
    return async (dispatch, getState) => {
        client.get(`${clientSupplyAccountSelectOptionsUrl(clientId)}`).then((response) => {
            dispatch(setAccountSelectOptions(response.data.supply_accounts))
        })
    }
}

export function fetchSupplyAccountSelectOptionById(id) {
    return async (dispatch, getState) => {
        client.get(`${supplyAccountSelectOptionByIdUrl}?supply_account_id=${id}`).then((response) => {
            dispatch(setAccountSelectOptions(response.data.supply_accounts))
        })
    }
}

export function fetchFilteredSupplyAccountSelectOptions(clientId, searchTerm) {
    return async (dispatch, getState) => {
        client.get(`${billFilteredSupplyAccountSelectOptionsUrl(clientId, searchTerm)}`).then((response) => {
            dispatch(setAccountSelectOptions(response.data.supply_accounts))
        })
    }
}

export function fetchSelectedAccount(accountType, accountId) {
    return async (dispatch, getState) => {
        dispatch(setAccountLoading(true))
        client.get(`${billAccountUrl(accountType, accountId)}`).then((response) => {
            dispatch(setAccount(response.data.account))
            dispatch(setAccountLoading(false))
        })
    }
}

export function fetchPreviousBillSelectOptions(accountType, accountId, date, billId = null) {
    // console.log(billId)
    return async (dispatch, getState) => {
        if (accountType === "BillableAccount") {
            // console.log(`${previousBillableAccountBillsUrl(accountId, date)}`)
            client.get(`${previousBillableAccountBillsUrl(accountId, date, billId)}`).then((response) => {
                dispatch(setPreviousBillSelectOptions(response.data.previous_bills))
            })
        } else {
            client.get(`${previousSupplyAccountBillsUrl(accountId, date, billId)}`).then((response) => {
                dispatch(setPreviousBillSelectOptions(response.data.previous_bills))
            })
        }
    }
}

export function fetchPreviousBill(id) {
    return async (dispatch, getState) => {
        client.get(billUrl(id)).then((response) => {
            dispatch(setPreviousBill(response.data))
        })
    }
}

export function fetchlinkedServiceBillSelectOptions(accountId) {
    return async (dispatch, getState) => {
        client.get(`${linkedServiceBillSelectOptionsUrl(accountId)}`).then((response) => {
            dispatch(setLinkedServiceBillSelectOptions(response.data.linked_service_bill_options))
        })
    }
}

export function fetchServiceBill(serviceBillId) {
    return async (dispatch, getState) => {
        // client.get(`${serviceBillUrl(serviceBillId)}`).then((response) => {
        //     dispatch(setAccount(response.data.account))
        // })

        const response = await client.get(`${serviceBillUrl(serviceBillId)}`)
        return response.data
    }
}

// TODO: will need to scope this as well
// export function fetchBillSelectOptions(searchTerm, currentPath) {
//     return async (dispatch, getState) => {
//         client.get(`${billSelectOptionsUrl}?search_term=${searchTerm}`).then((response) => {
//             dispatch(setBillSelectOptions(response.data.bills))
//         })
//     }
// }

// export function fetchBillSelectOptionById(id, currentPath) {
//     return async (dispatch, getState) => {
//         client.get(`${billSelectOptionByIdUrl}?bill_id=${id}`).then((response) => {
//             dispatch(setBillSelectOptions(response.data.bill))
//         })
//     }
// }

// export function searchBills(query) {
//     return async (dispatch) => {
//         dispatch(setLoading(true))

//         client
//             .get(`${billSearchUrl}?q=${query}`)
//             .then(({ data }) => {
//                 dispatch(setBillSearch(data))
//             })
//             .catch((error) => {
//                 console.warn(error)
//             })
//             .finally(() => {
//                 dispatch(setLoading(false))
//             })
//     }
// }

export function filterBills(query, { billableAccountId, supplyAccountId, groupAccountId } = {}, ) {
    /// create the filter URL string
    const url = groupAccountId ? groupAccountBillsScopedByClientUrl(query.clientId, groupAccountId) : billsScopedByClientUrl(query.clientId)
    let filterUrl = createFilterUrl(query, url)

    if (billableAccountId) {
        filterUrl += "&account_type=BillableAccount&account_id=" + billableAccountId
    } else if (supplyAccountId) {
        filterUrl += "&account_type=SupplyAccount&account_id=" + supplyAccountId
    }
    else if(groupAccountId){

    }

    return async (dispatch) => {
        dispatch(setLoading(true))
        client
            .get(filterUrl)
            .then((response) => {
                dispatch(setPagination(query.pagination))
                dispatch(setBills(response.data))
                dispatch(setLoading(false))
            })
            .catch((error) => {
                console.warn(error)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function generateCsv(clientId, filters) {
    return async (dispatch, getState) => {
        client.get(`${billsCsvUrl(clientId)}${createRansackParams(filters)}connection_id=${getState().app.uid}`)
    }
}

export function bulkDeleteBills(clientId, filters){
    return async (dispatch, getState) => {
        client.delete(`${clientUrl(clientId)}/bills/destroy_group`, {
            data: { filters }
        })
    }
}

export function handleCCisOk(billId, value){
    return async (dispatch, getState) => {
        await client.patch(`${billUrl(billId)}`, {
            data: { cc_is_ok: value },
        })
        await dispatch(fetchBill(billId))
    }
}

export const selectBill = (state) => state.bills.bill
export const selectBills = (state) => state.bills.bills
export const selectTotal = (state) => state.bills.total
export const selectPagination = (state) => state.bills.pagination
export const selectLoading = (state) => state.bills.loading
export const selectAccountLoading = (state) => state.bills.accountLoading
export const selectBillSelectOptions = (state) => state.bills.billSelectOptions
export const selectBillSearch = (state) => state.bills.billSearch
export const selectAccountSelectOptions = (state) => state.bills.accountSelectOptions
export const selectPreviousBillSelectOptions = (state) => state.bills.previousBillSelectOptions
export const selectPreviousBill = (state) => state.bills.previousBill
export const selectAccount = (state) => state.bills.account
export const selectLinkedServiceBillOptions = (state) => state.bills.linkedServiceBillSelectOptions
export const selectFilters = (state) => state.bills.filters
export const selectSort = (state) => state.bills.sort

export default billsSlice.reducer
